import { TranslocoService } from '@ngneat/transloco';
import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfirmationService, SelectItem, SelectItemGroup } from 'primeng/api';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { ORDER_DOCUMENT_TYPES } from 'projects/core/src/lib/constants';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { FileMode, FileSettings } from 'projects/core/src/lib/types/filesettings.type';
import { ResourceModel } from 'projects/core/src/lib/models/resource.model';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import FileSaver from 'file-saver';
import { DetailModes } from 'projects/admin/src/app/shared/constants';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import moment from 'moment';
import { UiService } from 'projects/admin/src/app/services/ui.service';


@Component({
  selector: 'app-order-detail-documents',
  templateUrl: './order-detail-documents.component.html',
  styleUrls: ['./order-detail-documents.component.scss']
})
export class OrderDetailDocumentsComponent implements OnInit, OnChanges {

  @Input() form: UntypedFormGroup;
  @Input() options: { [key: string]: Array<SelectItem | SelectItemGroup> };
  @Input() texts: { [key: string]: string };
  @Input() order: AcModel = null;
  @Input() mode: DetailModes | null;
  @Input() inEdit: boolean;
  @Input() submitting: boolean;

  public files: Array<File> = [];
  public settings: FileSettings = {};

  public fileForm: UntypedFormGroup = this.fb.group({});
  public optionsDocumenttypes: Array<SelectItem> = [];
  public optionsCandidates: Array<SelectItem> = [];
  public hasRight = false;

  constructor(
    private fb: UntypedFormBuilder,
    private confirmation: ConfirmationService,
    private helper: HelperService,
    private api: ApiService,
    private transloco: TranslocoService,
    private ui: UiService,
    public auth: AuthStateService,
  ) { }

  ngOnInit(): void {
    this.form.addControl('documents', this.fileForm);
    this.optionsDocumenttypes = FormHelper.getSelectList(ORDER_DOCUMENT_TYPES, this.transloco.translate('common.labels.please_choose'), this.transloco);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.order) {
      this.initData();
    }
  }

  initData(resources: Array<ResourceModel> = null): void {
    if (resources === null) {
      resources = this.order?.resources;
    }
    if (this.order) {
      // set candidate options
      this.optionsCandidates = this.order.candidates?.map((candidate: any) => {
        return {
          value: candidate.id,
          label: candidate.fullname,
        };
      }) || [];
    }

    this.settings = {};
    this.files = resources?.filter((resource: any) => {
      return resource.type === 'ac.file' || resource.type === 'candidate.file';
    }).map((resource: any) => {
      const fileId = `${resource.id}.${resource.extension}`;
      const control = new UntypedFormControl(resource.misc?.type, [Validators.required]);
      const candidateControl = new UntypedFormControl(resource.owner_id);
      this.settings[fileId] = {
        id: resource.id,
        name: resource.public_name,
        status: resource.misc?.status || null,
        size: this.helper.getFileSize(resource.misc?.filesize),
        type: resource.type,
        mode: FileMode.Edit,
        control,
        candidateControl,
        changable: true
      };
      if (this.fileForm.get(fileId)) {
        this.fileForm.get(fileId).setValue(resource.misc?.type);
        this.fileForm.get(fileId + '_candidate').setValue(resource.owner_id);
      } else {
        this.fileForm.addControl(fileId, control);
        this.fileForm.addControl(fileId + '_candidate', control);
      }
      return new File([], fileId);
    }) || [];
    this.fileForm.updateValueAndValidity();
    this.initRights();
  }

  public initRights(): void {
    this.hasRight =
      this.order?.status === undefined ||
      this.order?.status === null ||
      ['requested', 'planned', 'running'].indexOf(this.order?.status) !== -1 ||
      this.auth.hasRight('admin.planning-orders.add-doc-after-finish');
    this.order?.resources?.forEach((resource: any) => {
      const fileId = `${resource.id}.${resource.extension}`;
      const control = (this.settings[fileId]) ? this.settings[fileId].control : null;
      let fileRight = this.hasRight;
      if (this.order?.status === 'finished' && this.order.finished_at && control?.value !== 'man_report' && resource.created_at && moment(resource.created_at).isBefore(this.order.finished_at)) {
        fileRight = false;
      }
      if (control) {
        if (control.value === 'man_report' && resource.status === 'finished') {
          fileRight = false;
        }
        if (fileRight) {
          control.enable();
          control.setValidators([Validators.required]);
          control.updateValueAndValidity();
        } else {
          control.disable();
          control.clearValidators();
          control.updateValueAndValidity();
        }
      }
      if (this.settings[fileId]) {
        this.settings[fileId].changable = fileRight;
      }
    });
  }

  public getPayload(): any {
    const payload: any = {};
    Object.keys(this.settings).forEach((key: string) => {
      const setting = this.settings[key];
      if (setting !== undefined) {
        const candidateKey = setting.control?.value === 'man_report' ? String(setting.candidateControl.value) : 'AC';
        if (!payload[candidateKey]) {
          payload[candidateKey] = {};
        }
        payload[candidateKey][key] = {
          id: setting.id,
          name: setting.name,
          mode: setting.mode,
          type: setting.control?.value,
        };
      }
    });
    return payload;
  }

  public getFiles(): Array<File> {
    return this.files;
  }

  public setFiles(event: any): void {
    if (this.inEdit === true) {
      if (Array.isArray(event.currentFiles)) {
        event.currentFiles.forEach((file: File) => {
          if (this.settings[file.name] === undefined) {
            const candidate = this.order?.candidates?.length === 1 ? this.order?.candidates[0] : null;
            const control = new UntypedFormControl('', [Validators.required]);
            const candidateControl = new UntypedFormControl(candidate?.id || null);
            this.settings[file.name] = {
              id: null,
              name: file.name,
              size: this.helper.getFileSize(file.size),
              mode: FileMode.Create,
              control,
              candidateControl,
              changable: true
            };
            if (!this.fileForm.get(file.name)) {
              this.fileForm.addControl(file.name, control);
              control.updateValueAndValidity();
              this.fileForm.addControl(file.name + '_candidate', candidateControl);
              candidateControl.updateValueAndValidity();
            }
          }
        });
      }
      this.files = event.currentFiles;
      this.fileForm.updateValueAndValidity();
      this.initRights();
    }
  }

  public removeFile(file: File): void {
    if (this.inEdit === true) {
      this.confirmation.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: this.settings[file.name]?.name }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.files = this.files.filter((actualFile: File) => {
            return file.name !== actualFile.name;
          });
          delete this.settings[file.name];
          this.fileForm.removeControl(file.name);
          this.fileForm.removeControl(file.name + '_candidate');
        }
      });
      this.fileForm.updateValueAndValidity();
    }
  }

  public downloadFile(id: number, fileName: string, type: string, owner_id: number = null): void {
    this.api
      .downloadFile(
        id,
        type,
        owner_id || this.order.id,
      )
      .subscribe({
        next: (response: any) => {
          const blob = new Blob([response]);
          FileSaver.saveAs(blob, fileName);
        }
      });
  }

  public clearFiles(): void {
    if (this.inEdit === true) {
      this.files = [];
    }
  }

  public getCandidateName(candidateId: number): string {
    const candidate = this.order?.candidates?.find((c: any) => c.id === candidateId);
    return candidate ? candidate.fullname : '';
  }

  public finishReport(file: any): void {
    if (file?.candidateControl?.value)
    this.confirmation.confirm({
      message: this.transloco.translate('common.messages.really_finish_resultreport', { value: file.name }),
      header: this.transloco.translate('common.info'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.api.finishResultreport(this.order.id, file.candidateControl.value).subscribe({
          next: (result: any) => {
            file.status = 'finished';
            this.ui.toastSuccess(this.transloco.translate('common.messages.finish_resultreport_success'));
          },
          error: (error: any) => {
            this.ui.toastError(this.transloco.translate('common.messages.finish_resultreport_error'));
          }
        });
      }
    });
  }

  public isFinishable(file: any): boolean {
    return this.inEdit !== true &&
      this.settings[file.name].control?.value === 'man_report' &&
      this.settings[file.name].status === 'progress' &&
      file.name?.toLowerCase().endsWith('.pdf');
  }

}
